import { createBrowserRouter, type RouteObject } from 'react-router-dom';

import { HomePage } from '../features/home/HomePage';
import {
  AccommodationDetailPage,
  AccommodationsPage,
  ActivitiesHubPage,
  ActivityDetailPage,
  ActivityTypePage,
  BrochureListPage,
  DiscoverHubPage,
  EventDetailPage,
  EventsPage,
  ItineraryDetailPage,
  ItineraryListPage,
  NotFoundPage,
  PageView,
  PostDetailPage,
  PostListPage,
  ProjectDetailPage,
  ProjectListPage,
  RegionPage,
  ReportListPage,
  SearchPage,
  TrailDetailPage,
  TrailListPage,
  WhatToSeeHubPage,
} from './lazyPages';
import { LocaleLayout } from './LocaleLayout';
import { SiteLayout } from './SiteLayout';

/**
 * Locale-prefixed routing (§5). The route tree below is defined ONCE and mounted twice:
 * unprefixed for English, under `/:locale` for Georgian. Duplicating the tree per locale
 * would guarantee the two drift apart as ~40 routes land across M1–M5.
 *
 * Only the routing mechanism and a couple of placeholders exist at M0 — the real pages
 * arrive with their milestones (§22).
 */
const siteRoutes: RouteObject[] = [
  { index: true, element: <HomePage /> },

  // Discover (M3, §7.2/§7.3) — hub reachable only from the Home hero; info pages by slug.
  { path: 'discover', element: <DiscoverHubPage /> },
  { path: 'discover/:slug', element: <PageView /> },

  // What to See (M1)
  { path: 'what-to-see', element: <WhatToSeeHubPage /> },
  { path: 'what-to-see/:region', element: <RegionPage /> },

  // Things to Do — Activities & Tours (M2, §7.6–7.8)
  { path: 'what-to-do/activities-and-tours', element: <ActivitiesHubPage /> },
  { path: 'what-to-do/activities-and-tours/:type', element: <ActivityTypePage /> },
  { path: 'what-to-do/activities-and-tours/:type/:activity', element: <ActivityDetailPage /> },

  // Things to Do — Agritourism (M2, §7.9): flat detail route, fixed type slug
  {
    path: 'what-to-do/agritourism',
    element: <ActivityTypePage fixedSlug="agritourism" detailBase="/what-to-do/agritourism" />,
  },
  { path: 'what-to-do/agritourism/:activity', element: <ActivityDetailPage /> },

  // Things to Do — Hiking Trails (M2, §7.10–7.11)
  { path: 'what-to-do/hiking-trail-map', element: <TrailListPage /> },
  { path: 'what-to-do/hiking-trail-map/:slug', element: <TrailDetailPage /> },

  // Things to Do — Events (M2, §7.12–7.13)
  { path: 'what-to-do/events', element: <EventsPage /> },
  { path: 'what-to-do/events/:event', element: <EventDetailPage /> },

  // Stay & Eat (M2, §7.14): one template per type + shared detail
  { path: 'stay-eat/hotels-resorts', element: <AccommodationsPage type="hotel_resort" titleKey="accommodations.hotelsResorts" /> },
  { path: 'stay-eat/guest-houses', element: <AccommodationsPage type="guest_house" titleKey="accommodations.guestHouses" /> },
  { path: 'stay-eat/wine-culinary', element: <AccommodationsPage type="wine_culinary" titleKey="accommodations.wineCulinary" /> },
  { path: 'stay-eat/restaurants', element: <AccommodationsPage type="restaurant" titleKey="accommodations.restaurants" /> },
  { path: 'stay-eat/:slug', element: <AccommodationDetailPage /> },

  // DMO — News (§7.17). Posts of type=news.
  {
    path: 'destination-management/news',
    element: (
      <PostListPage
        type="news"
        titleKey="posts.newsTitle"
        headingKey="posts.newsHeading"
        basePath="/destination-management/news"
      />
    ),
  },
  { path: 'destination-management/news/:slug', element: <PostDetailPage /> },

  // DMO — Projects (§7.17).
  { path: 'destination-management/projects', element: <ProjectListPage /> },
  { path: 'destination-management/projects/:slug', element: <ProjectDetailPage /> },

  // DMO — Reports (§7.17). Flat list; cards open the PDF.
  { path: 'destination-management/reports', element: <ReportListPage /> },

  // DMO — About The DMO (§7.17, info template CMS page).
  { path: 'destination-management/about', element: <PageView fixedSlug="about-the-dmo" /> },

  // Offers (§7.19) — five coming-soon pages, one per slug.
  { path: 'offers/:slug', element: <PageView /> },

  // Blog (§7.18). Posts of type=blog.
  {
    path: 'blog',
    element: (
      <PostListPage
        type="blog"
        titleKey="posts.blogTitle"
        headingKey="posts.blogHeading"
        headingAccentKey="posts.blogHeadingAccent"
        basePath="/blog"
      />
    ),
  },
  { path: 'blog/:slug', element: <PostDetailPage /> },

  // Plan → Itineraries & Tours (§7.16).
  { path: 'plan/itineraries', element: <ItineraryListPage /> },
  { path: 'plan/itineraries/:slug', element: <ItineraryDetailPage /> },

  // Plan → Brochures (§7.15).
  { path: 'plan/brochures', element: <BrochureListPage /> },

  // Legal (§6.14 legal template) — footer links.
  { path: 'privacy-policy', element: <PageView fixedSlug="privacy-policy" /> },
  { path: 'terms-of-service', element: <PageView fixedSlug="terms-of-service" /> },
  { path: 'cookie-policy', element: <PageView fixedSlug="cookie-policy" /> },

  // Global search (§14) — the query lives in ?q= so results are linkable.
  { path: 'search', element: <SearchPage /> },

  { path: '*', element: <NotFoundPage /> },
];

export const router = createBrowserRouter([
  {
    // English — unprefixed.
    element: <LocaleLayout />,
    children: [{ path: '/', element: <SiteLayout />, children: siteRoutes }],
  },
  {
    // Georgian (and any other future locale) — prefixed.
    path: '/:locale',
    element: <LocaleLayout />,
    children: [{ path: '', element: <SiteLayout />, children: siteRoutes }],
  },
]);
